import { Comparable, Organisation, Trackable } from '@stylehaven/components';

export enum ManagementEntityType {
    Users = 'users',
    Zones = 'zones',
    Groups = 'groups',
    Roles = 'roles',
    ExportLocations = 'export-locations',
    FieldDefinitions = 'field-definitions',
    MetadataProfiles = 'profiles',
    MetadataProfileFields = 'fields',
    FacetProfiles = 'facets',
    AdvSearch = 'advanced-search',
    Organisation = 'organisation',
    GenericList = 'columns' // Calling this column since this will be visible in the url
}

export class PaginatedEntity<T extends { Id?: string; [_: string]: any } | null> implements Comparable, Trackable {
    constructor(
        public Item: T,
        public EntityType: ManagementEntityType,
        public Organisation?: Organisation,
        public UserCount?: number
    ) {}

    public setOrganisation(organisation: Organisation): this {
        this.Organisation = organisation;
        return this;
    }

    public setUserCount(userCount: number): this {
        this.UserCount = userCount;
        return this;
    }

    public static getBaseUrl(entityType: ManagementEntityType): string {
        let rootPart: string;
        switch (entityType) {
            case ManagementEntityType.Groups:
            case ManagementEntityType.Roles:
            case ManagementEntityType.Users:
            case ManagementEntityType.Zones:
            case ManagementEntityType.Organisation:
                rootPart = 'user-management';
                break;
            case ManagementEntityType.ExportLocations:
                rootPart = 'export';
                break;
            case ManagementEntityType.FieldDefinitions:
            case ManagementEntityType.MetadataProfiles:
                rootPart = 'metadata';
                break;
            case ManagementEntityType.MetadataProfileFields:
                rootPart = 'metadata/profiles';
                break;
            case ManagementEntityType.AdvSearch:
            case ManagementEntityType.FacetProfiles:
            case ManagementEntityType.GenericList:
                rootPart = 'search';
                break;
            default:
                console.warn(`No url configured for ${entityType}`);
                rootPart = '';
        }
        return `/${rootPart}/${entityType}`;
    }

    public static getCreateUrl(entityType: ManagementEntityType): string {
        let subPath: string = '';
        if (
            [
                ManagementEntityType.FacetProfiles,
                ManagementEntityType.AdvSearch,
                ManagementEntityType.GenericList
            ].includes(entityType)
        ) {
            subPath = 'fields/';
        }
        return `${this.getBaseUrl(entityType)}/${subPath}create`;
    }

    public getDetailUrl(subPath?: string): string {
        return `${PaginatedEntity.getBaseUrl(this.EntityType)}/${this.trackBy()}${subPath ? '/' + subPath : ''}`;
    }

    public getEditUrl(subPath?: string): string {
        return this.getDetailUrl(subPath) + '/edit';
    }

    equals(other: any): boolean {
        return this.Item?.Id === other.Item.Id;
    }

    trackBy(): string {
        return this.Item?.Id ?? '';
    }
}
